import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const SessionControlPanel = ({ 
  onPause,
  onResume,
  onSkipQuestion,
  onEndSession,
  onOpenNotes,
  isPaused = false,
  canSkip = true,
  sessionMode = 'practice'
}) => {
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  const handlePauseToggle = () => {
    if (isPaused) {
      onResume?.();
    } else {
      onPause?.();
    }
  };

  const handleSkipQuestion = () => {
    if (canSkip) {
      setShowSkipConfirm(true);
    }
  };

  const confirmSkip = () => {
    onSkipQuestion?.();
    setShowSkipConfirm(false);
  };

  const handleEndSession = () => {
    setShowExitConfirm(true);
  };

  const confirmEndSession = () => {
    onEndSession?.();
    setShowExitConfirm(false);
  };

  const handleOpenNotes = () => {
    onOpenNotes?.();
  };

  return (
    <>
      {/* Main Control Panel */}
      <div style={{left:'0'}} className="fixed bottom-4  z-50">
        <div  className="hidden md:flex items-center transition-all duration-300 rounded-full overflow-hidden  hover:w-full hover:pr-2 group">
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gradient-to-r from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10 rounded-full px-3 py-3 flex items-center space-x-2 group-hover:space-x-3 backdrop-blur">
              {/* Pause/Resume Button */}
              <Button
                variant="ghost"
                size="sm"
                iconName={isPaused ? "Play" : "Pause"}
                onClick={handlePauseToggle}
                className={`min-w-10 ${isPaused ? 'text-green-400 hover:text-green-300' : 'text-gray-400 hover:text-white'}`}
              >
                <span className="hidden group-hover:inline ml-1">
                  {isPaused ? 'Resume' : 'Pause'}
                </span>
              </Button>

              {/* Skip Question Button */}
              <Button
                variant="ghost"
                size="sm"
                iconName="SkipForward"
                onClick={handleSkipQuestion}
                disabled={!canSkip}
                className="min-w-10 text-gray-400 hover:text-white disabled:text-gray-600"
              >
                <span className="hidden group-hover:inline ml-1">
                  Skip
                </span>
              </Button>

              {/* Notes Button */}
              <Button
                variant="ghost"
                size="sm"
                iconName="FileText"
                onClick={handleOpenNotes}
                className="min-w-10 text-gray-400 hover:text-white hidden lg:flex"
              >
                <span className="hidden group-hover:inline ml-1">
                  Notes
                </span>
              </Button>

              {/* Settings Button */}
              <Button
                variant="ghost"
                size="sm"
                iconName="Settings"
                className="min-w-10 text-gray-400 hover:text-white hidden lg:flex"
              >
                <span className="hidden group-hover:inline ml-1">
                  Settings
                </span>
              </Button>

              {/* End Session Button */}
              <Button
                variant="ghost"
                size="sm"
                iconName="Square"
                onClick={handleEndSession}
                className="min-w-10 text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <span className="hidden group-hover:inline ml-1">
                  End
                </span>
              </Button>
            </div>
          </SpotlightCard>
        </div>
      </div>

      {/* Mobile Control Panel */}
      <div className="fixed bottom-4 left-0 right-0 z-50 md:hidden px-4">
        <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
          <div className="bg-gradient-to-r from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10 rounded-full px-4 py-3 flex justify-between items-center backdrop-blur">
            <Button
              variant={isPaused ? "default" : "outline"}
              size="sm"
              iconName={isPaused ? "Play" : "Pause"}
              onClick={handlePauseToggle}
              className={`text-xs ${isPaused ? 'bg-gradient-to-r from-purple-500 to-indigo-600' : 'border-gray-600 text-gray-300'}`}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </Button>

            <Button
              variant="outline"
              size="sm"
              iconName="SkipForward"
              onClick={handleSkipQuestion}
              disabled={!canSkip}
              className="text-xs border-gray-600 text-gray-300 hover:text-white disabled:text-gray-600"
            >
              Skip
            </Button>

            <Button
              variant="outline"
              size="sm"
              iconName="FileText"
              onClick={handleOpenNotes}
              className="text-xs border-gray-600 text-gray-300 hover:text-white"
            >
              Notes
            </Button>

            <Button
              variant="destructive"
              size="sm"
              iconName="Square"
              onClick={handleEndSession}
              className="text-xs"
            >
              End
            </Button>
          </div>
        </SpotlightCard>
      </div>

      {/* Skip Question Confirmation Modal */}
      {showSkipConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.2)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10 rounded-lg p-6 max-w-md mx-4 backdrop-blur">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-yellow-400" />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Skip This Question?</h3>
                  <p className="text-sm text-gray-300">
                    Skipping questions may affect your overall performance score. Are you sure you want to continue?
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSkipConfirm(false)}
                    className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={confirmSkip}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    Skip Question
                  </Button>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </div>
      )}

      {/* End Session Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.2)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10 rounded-lg p-6 max-w-md mx-4 backdrop-blur">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
                  <Icon name="AlertCircle" size={24} className="text-red-400" />
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">End Interview Session?</h3>
                  <p className="text-sm text-gray-300">
                    Your progress will be saved, but you won't be able to continue this session. You can review your performance in the results page.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowExitConfirm(false)}
                    className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                  >
                    Continue Session
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={confirmEndSession}
                    className="flex-1"
                  >
                    End Session
                  </Button>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </div>
      )}

      {/* Pause Overlay */}
      {isPaused && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center border border-purple-500/30">
              <Icon name="Pause" size={40} className="text-purple-400" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Session Paused</h2>
              <p className="text-gray-300">Take your time. Click resume when you're ready to continue.</p>
            </div>

            <Button
              variant="default"
              size="lg"
              iconName="Play"
              onClick={handlePauseToggle}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              Resume Interview
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionControlPanel;
